import { useQuery } from '@tanstack/react-query';
import { getSubscriptionMe } from '../api/subscriptions.js';
import { UpgradeCta } from './UpgradeCta.js';

/**
 * Employee-cap warning banner (Phase D-4). Admin-layout only — employees don't
 * create employees. Driven by GET /v1/subscriptions/me (`employeeCount` +
 * `employeeCap`); shares the `['subscription','me']` query cache with
 * SubscriptionBanner, so this is not a second network call.
 *
 * Locked decision (Option B, 2026-06-08): hard 402 block at the cap (enforced
 * server-side); a warning banner at 90% utilization. Threshold = ceil(cap*0.9),
 * so 45 for a Starter cap of 50; it scales with per-tenant overrides.
 *
 * Renders nothing when: data not loaded; cap is null (unlimited); count below
 * the 90% threshold; or the subscription is already blocked (read-only/
 * suspended/cancelled — SubscriptionBanner owns that message, and writes are
 * blocked anyway, so a cap warning would be noise).
 */
export function CapWarningBanner(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['subscription', 'me'],
    queryFn: getSubscriptionMe,
    retry: 1,
    staleTime: 60_000,
  });

  if (!data) return null;

  const blocked =
    data.effectiveStatus === 'read_only' ||
    data.effectiveStatus === 'suspended' ||
    data.effectiveStatus === 'cancelled';
  if (blocked) return null;

  // A payment-overdue (past_due) tenant already sees SubscriptionBanner's
  // "payment overdue" warning, which takes precedence — the fix is to renew,
  // not to add seats. Suppress the cap warning so the two don't compete.
  if (data.status === 'past_due') return null;

  const { employeeCount, employeeCap } = data;
  if (employeeCap === null) return null; // unlimited — never warn

  const threshold = Math.ceil(employeeCap * 0.9);
  if (employeeCount < threshold) return null;

  const atCap = employeeCount >= employeeCap;

  return (
    <>
      <div
        role="alert"
        className={`w-full px-4 py-2 text-sm border-b ${
          atCap ? 'bg-red-100 text-red-900 border-red-300' : 'bg-amber/10 text-amber border-amber/30'
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <span>
            {atCap ? (
              <>
                You&apos;ve used all{' '}
                <strong>
                  {employeeCap} seat{employeeCap === 1 ? '' : 's'}
                </strong>{' '}
                on your plan. Add seats or upgrade to add more employees.
              </>
            ) : (
              <>
                You&apos;re using{' '}
                <strong>
                  {employeeCount} of {employeeCap}
                </strong>{' '}
                seats. Add seats or upgrade to keep growing.
              </>
            )}
          </span>
          <UpgradeCta tier={data.tier} label="Manage plan" />
        </div>
      </div>
    </>
  );
}
